import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, Validators } from "@angular/forms";
import { KnownKeys } from "app/shared/model/properties.model";
import { createSentinelFacility, RequiredSentinelFacility, SentinelFacility } from "ng/src/app/log-settings/provisioning/sentinel-provision/sentinel-provision.model";
import { SentinelProvisioningService } from "ng/src/app/log-settings/provisioning/sentinel-provision/sentinel-provisioning.service";
import { SelectorItem } from "ng/src/app/shared/item-selectors/item-selector.model";
import { FormControlMap, RealFormControl } from "ng/src/app/shared/model/app.model";
import { ConfirmationService, PrimeIcons } from "primeng/api";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";

@Component({
  selector: 'facility-editor',
  templateUrl: './facility-editor.component.html',
  styleUrls: ['./facility-editor.component.scss']
})
export class FacilityEditorComponent implements OnInit, OnDestroy {
  formMap?: FormControlMap<EditorSentinelFacility> | undefined;
  readonly icons = PrimeIcons;
  
  errorMessage?: string;
  
  readonly _destroy = new Subject<void>();
  
  constructor(
    public sentinelProvisioningService: SentinelProvisioningService,
    private changeRef: ChangeDetectorRef,
    private confirmationService: ConfirmationService
  ) { }
  
  stopEditing() {
    this.sentinelProvisioningService.editingFacility.next(undefined);
  }
  
  validateAndUpdateError() {
    const errors = this.validate();
    if (errors.length) {
      this.errorMessage = 'There are missing values.';
    } else {
      this.errorMessage = undefined;
    }
    
    this.changeRef.detectChanges();
    return errors;
  }
  
  /**
   * Validates the form and marks all controls as touched and dirty.
   * @returns An array of controls that have errors.
   */
  validate(): AbstractControl[] {
    if (!this.formMap) {
      return [];
    }
    
    const foundErrors: AbstractControl[] = [];
    checkMap(this.formMap);
    
    return foundErrors;
    
    function checkMap(map: object) {
      const keys = Object.keys(map) as KnownKeys<typeof map>[];
      for (const key of keys) {
        const entry = map[key];
        
        if (Array.isArray(entry)) {
          for (const subEntry of (entry as any[])) {
            if (subEntry instanceof AbstractControl) {
              checkControl(subEntry);
            }
            else {
              checkMap(subEntry);
            }
          }
        } else if ((entry as object) instanceof AbstractControl) {
          checkControl(entry);
        } else if (typeof entry === 'object' && entry) {
          checkMap(entry);
        }
      }
    }
    
    function checkControl(control: unknown) {
      if (control instanceof AbstractControl) {
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity();
        const errors = control.errors;
        if (errors) {
          foundErrors.push(control);
        }
      }
    }
  }
  
  /**
   * Creates a form control map for the provided facility by filling in using createdFilledFacility.
   * @param facility The facility to create the form control map for.
   */
  createFormControlMap(facility: SentinelFacility): FormControlMap<EditorSentinelFacility> {
    const self = this;
    const filledFacility = this.createFilledFacility(facility);
    const keys = Object.keys(filledFacility) as (keyof RequiredSentinelFacility)[]
    return keys.reduce((map, key) => {
      if (key === 'dataEntry') {
        const filledData = filledFacility.dataEntry;
        map['dataEntry'] = {
          firstName: createControl(filledData.firstName),
          lastName: createControl(filledData.lastName),
          company: createControl(filledData.company),
          isCitizen: createControl(filledData.isCitizen),
          nda: createControl(filledData.nda),
          reason: createControl(filledData.reason),
          visiting: createControl(filledData.visiting),
          telephone: createControl(filledData.telephone),
        }
      } else if (key === 'checkoutTime') {
        if (filledFacility.checkoutTime == null) {
          map['checkoutTime'] = createControl(null);
          return map;
        }
        else {
          try {
            const now = new Date();
            const input = filledFacility.checkoutTime;
            const split = input.split('');
            const hours = parseInt(split.slice(0, 2).join(''));
            const minutes = parseInt(split.slice(3, 5).join(''));
            
            if (isNaN(hours) || isNaN(minutes)) {
              // noinspection ExceptionCaughtLocallyJS
              throw new Error(`Invalid time ${input}`);
            }
            
            now.setHours(hours, minutes, 0, 0);
            map['checkoutTime'] = createControl(now, key);
          } catch (e) {
            console.error('Error parsing checkout time', e);
            map['checkoutTime'] = createControl(null, key);
          }
        }
      } else if (key === 'accessCodes') {
        map['accessCodes'] = filledFacility.accessCodes.map((a) => {
          return {
            code: new FormControl(a.code),
            label: new FormControl(a.label),
          };
        });
      } else {
        map[key] = createControl(filledFacility[key], key) as any;
      }
      return map;
    }, {} as unknown as FormControlMap<EditorSentinelFacility>);
    
    function createControl<T>(value: T, key?: keyof typeof filledFacility): RealFormControl<T> {
      const control = new FormControl<T>(value);
      const notRequired = new Set<keyof RequiredSentinelFacility>([
        'logoImage', 'badgeLogoImage', 'id', 'log'
      ] as const);
      
      if (!key || key && !notRequired.has(key)) {
        control.addValidators(Validators.required);
      }
      
      if (key) {
        switch (key) {
          case 'checkoutHrs': {
            control.addValidators((c) => {
              
              const val = c.value as number | null;
              if (!self.formMap) {
                return null;
              }
              
              if (self.formMap.autoCheckout.value === 'timed') {
                return null;
              }
              
              return val != null ? null : {invalid: 'Checkout hours must be set'};
            });
            break;
          }
          case 'checkoutTime': {
            control.addValidators((c) => {
              const val = c.value as Date | null;
              if (!self.formMap) {
                return null;
              }
              
              if (self.formMap.autoCheckout.value === 'fixed') {
                return null;
              }
              
              return val != null ? null : {invalid: 'Checkout time must be set'};
            });
          }
        }
      }
      control.statusChanges.pipe(
        takeUntil(self._destroy),
      ).subscribe(() => {
        console.log(control.value);
        self.changeRef.detectChanges();
      });
      
      return control;
    }
  }
  
  editorSentinelFacilityToFacility(facility: FormControlMap<EditorSentinelFacility>): RequiredSentinelFacility {
    const keys = Object.keys(facility) as KnownKeys<typeof facility>[];
    
    return keys.reduce((map, key) => {
      if (key === 'dataEntry') {
        const dataEntry = facility.dataEntry;
        map['dataEntry'] = {
          firstName: dataEntry.firstName.value as any,
          lastName: dataEntry.lastName.value as any,
          company: dataEntry.company.value as any,
          isCitizen: dataEntry.isCitizen.value as any,
          nda: dataEntry.nda.value as any,
          reason: dataEntry.reason.value as any,
          visiting: dataEntry.visiting.value as any,
          telephone: dataEntry.telephone.value as any,
        }
      } else if (key === 'checkoutTime') {
        const time = facility.checkoutTime.value;
        if (time == null) {
          map['checkoutTime'] = '';
        } else {
          map['checkoutTime'] = `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}`;
        }
      } else if (key === 'accessCodes') {
        map['accessCodes'] = facility.accessCodes.map((a) => {
          return {
            code: a.code.value as string,
            label: a.label.value as string,
          };
        });
      } else {
        map[key] = facility[key]?.value as never;
      }
      return map;
    }, {} as RequiredSentinelFacility);
  }
  
  /**
   * Create a new facility with all fields filled in if they were undefined from the provided facility.
   * @param facility The facility to fill in.
   */
  createFilledFacility(facility: SentinelFacility): RequiredSentinelFacility {
    const newFacility = createSentinelFacility();
    return {
      ...newFacility,
      ...facility,
      dataEntry: {
        ...newFacility.dataEntry,
        ...facility.dataEntry,
      }
    };
  }
  
  addAccessCode() {
    const map = this.formMap?.accessCodes;
    if (!map) {
      console.error('No form control for access codes');
      return;
    }
    
    const access = {
      code: new FormControl('', Validators.required),
      label: new FormControl('', Validators.required)
    };
    
    map.splice(0, 0, access);
  }
  
  removeAccessCode(code: FormControlMap<{ code: string, label: string }>) {
    const idxOf = this.formMap?.accessCodes.indexOf(code);
    if (idxOf == null || idxOf < 0) {
      return;
    }
    
    this.formMap?.accessCodes.splice(idxOf, 1);
  }
  
  onFileUpload(ev: Event) {
    const formControl = this.formMap?.logoImage;
    if (!formControl) {
      console.error('No form control for logo image');
      return;
    }
    
    const input = ev.target as HTMLInputElement;
    
    if (input.files) {
      const file = input.files[0];
      if (!(file instanceof File)) {
        return;
      }
      
      if (file.size > FacilityEditorComponent.MAX_FILE_SIZE) {
        this.confirmationService.confirm({
          icon: this.icons.EXCLAMATION_TRIANGLE,
          message: 'The file is too large. The maximum file size is 500KB.',
          rejectVisible: false,
          acceptButtonStyleClass: 'p-button-secondary',
          defaultFocus: 'accept',
          acceptLabel: 'Dismiss'
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          formControl.setValue(result);
        }
      };
      
      reader.readAsDataURL(file);
    }
  }
  
  removeLogo() {
    this.confirmationService.confirm({
      icon: this.icons.EXCLAMATION_TRIANGLE,
      message: 'Are you sure you want to remove the logo?',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      defaultFocus: 'reject',
      accept: () => {
        this.formMap?.logoImage.setValue(null);
      }
    })
  }
  
  save() {
    if (!this.formMap) {
      return Promise.reject(new Error('No form map'));
    }
    
    if (this.validate().length) {
      return Promise.reject(new Error('Form has errors'));
    }
    
    const facility = this.editorSentinelFacilityToFacility(this.formMap);
    return this.sentinelProvisioningService.saveFacility(facility, true);
  }
  
  ngOnInit(): void {
    this.sentinelProvisioningService.editingFacility.pipe(
      takeUntil(this._destroy),
      filter((v) => v != null),
    ).subscribe((facility) => {
      this.formMap = this.createFormControlMap(facility!);
    });
  }
  
  ngOnDestroy(): void {
    this._destroy.next();
  }
  
  readonly checkoutOptions = [
    SelectorItem.with('timed', 'After visitors have been checked in for a number of hours'),
    SelectorItem.with('fixed', 'At a specific time everyday'),
    SelectorItem.with('disabled', 'Disabled')
  ] as const;
  
  readonly requiredOptional = [
    SelectorItem.with('required', 'Required'),
    SelectorItem.with('optional', 'Optional')
  ] as const;
  
  readonly requiredDisabled = [
    SelectorItem.with('required', 'Required'),
    SelectorItem.with('disabled', 'Disabled')
  ] as const;
  
  readonly requiredOptionalDisabled = [
    SelectorItem.with('required', 'Required'),
    SelectorItem.with('optional', 'Optional'),
    SelectorItem.with('disabled', 'Disabled')
  ] as const;
  
  readonly visitorPhotoOptions = [
    SelectorItem.with('required', 'Required'),
    SelectorItem.with('optional', 'Optional'),
    SelectorItem.with('notallowed', 'Disabled')
  ] as const;
  
  static readonly MAX_FILE_SIZE = 1024 * 1024 * 0.5; // 500KB
}

type EditorSentinelFacility = Omit<RequiredSentinelFacility, 'checkoutTime'> & {
  checkoutTime: Date | null
}
